import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { Delete as DeleteIcon, DragIndicator as DragIndicatorIcon, Edit as EditIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  dragHandle: {
    display: "flex",
    alignItems: "center",
    color: theme.palette.text.secondary,
    cursor: "grab",
    marginTop: 2,
    touchAction: "none",
  },
  content: {
    flex: 1,
    minWidth: 0,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: 14,
  },
  rowActions: {
    display: "flex",
    flexDirection: "column",
  },
  editActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}));

// The plain-text sibling of a song row - same drag handle, same divider,
// but click-to-edit (a multiline textarea, no rich-text controls - see
// AddNoteControl) instead of navigating anywhere, and a delete button
// behind a confirmation (this app's established policy for every
// destructive action).
export default ({ note, canEdit, showDivider, draggableRef, draggableProps, dragHandleProps, onUpdate, onDelete }) => {
  const classes = useStyles();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(note.content);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const startEdit = () => {
    setDraft(note.content);
    setEditing(true);
  };

  const saveEdit = () => {
    onUpdate(draft);
    setEditing(false);
  };

  if (editing) {
    return (
      <ListItem ref={draggableRef} divider={showDivider} {...draggableProps}>
        <Box style={{ width: "100%" }}>
          <TextField
            multiline
            fullWidth
            minRows={3}
            variant="outlined"
            color="secondary"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            autoFocus
          />
          <Box className={classes.editActions}>
            <Button size="small" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button size="small" color="secondary" onClick={saveEdit} disabled={!draft.trim()}>
              Save
            </Button>
          </Box>
        </Box>
      </ListItem>
    );
  }

  return (
    <ListItem ref={draggableRef} divider={showDivider} {...draggableProps} className={classes.row}>
      {canEdit && dragHandleProps && (
        <span className={classes.dragHandle} {...dragHandleProps}>
          <DragIndicatorIcon fontSize="small" />
        </span>
      )}
      <Typography component="div" className={classes.content}>
        {note.content}
      </Typography>
      {canEdit && (
        <Box className={classes.rowActions}>
          <IconButton size="small" onClick={startEdit} title="Edit">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => setConfirmDeleteOpen(true)} title="Delete">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Remove this text block?</DialogTitle>
        <DialogContent>
          <DialogContentText>This can't be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} autoFocus>
            Cancel
          </Button>
          <Button
            style={{ color: "#f44336" }}
            onClick={() => {
              setConfirmDeleteOpen(false);
              onDelete();
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </ListItem>
  );
};
